// Компонент для печати QR-кодов коробов

import React, { useRef } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Print as PrintIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { BoxCode } from './types';

interface BoxQRPrintViewProps {
  boxes: BoxCode[];
  documentNumber: string;
  pharmacyNum: string;
  toPharmacyNum?: string; // Аптека-получатель (для перемещений)
  documentType: number; // Тип документа (4 - возврат, 5 - перемещение)
  throughRC?: boolean | null; // Через РЦ
  routeText?: string; // Полный маршрут (напр. "Аптека №5 → Склад Алматы → Аптека №12")
  onNext: () => void;
  onBack: () => void;
}

const BoxQRPrintView: React.FC<BoxQRPrintViewProps> = ({
  boxes,
  documentNumber,
  pharmacyNum,
  toPharmacyNum,
  documentType,
  throughRC,
  routeText,
  onNext,
  onBack
}) => {
  const printContentRef = useRef<HTMLDivElement>(null);

  // Тип перемещения label
  const movementLabel = documentType === 5
    ? (throughRC ? 'ЧЕРЕЗ СКЛАД' : 'ПРЯМОЕ ПЕРЕМЕЩЕНИЕ')
    : 'ВОЗВРАТ';

  const handlePrint = () => {
    if (!printContentRef.current) return;

    // Собираем SVG QR-кодов из текущего DOM
    const svgElements = printContentRef.current.querySelectorAll('svg');
    const svgDataMap: Record<number, string> = {};
    svgElements.forEach((svg, index) => {
      svgDataMap[index] = new XMLSerializer().serializeToString(svg);
    });

    const today = new Date().toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Генерируем HTML-карточки — каждая на 100% ширины, друг под другом
    const cardsHtml = boxes.map((box, index) => {
      return `
        <div style="
          border:2px dashed #999;
          border-radius:12px;
          padding:20px;
          position:relative;
          display:flex;
          align-items:center;
          gap:24px;
          break-inside:avoid;
          page-break-inside:avoid;
          background:white;
          margin-bottom:16px;
        ">
          <!-- QR-код слева -->
          <div style="flex-shrink:0; max-width:100%; max-height:100%; display:flex; align-items:center; justify-content:center;">
            ${svgDataMap[index] || ''}
          </div>

          <!-- Информация справа -->
          <div style="flex:1;display:flex;flex-direction:column;">
            <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:4px">
              <div style="font-size:1.2rem;font-weight:900;color:#1976d2;line-height:1">
                №${documentNumber}
              </div>
              <div style="font-size:1rem;font-weight:800;color:#333;background:#f5f5f5;padding:4px 8px;border-radius:4px;line-height:1">
                Короб ${box.box_num} / ${boxes.length}
              </div>
            </div>
            
            <div style="display:inline-block; align-self:flex-start; font-size:0.8rem;font-weight:800;color:#1976d2;background:#e3f2fd;border-radius:6px;text-transform:uppercase;letter-spacing:0.5px;margin:2px 0">
              ${movementLabel}
            </div>

            ${routeText ? `
              <div style="font-size:1rem;color:#444;margin-top:6px;line-height:1.4">
                <span style="font-weight:700;color:#777">МАРШРУТ<br/></span> ${routeText.replace(/ → /g, ' <span style="color:#1976d2;font-weight:bold">→</span> ')}
              </div>
            ` : `
              <div style="font-size:1rem;color:#444;margin-top:6px;line-height:1.4">
                <span style="font-weight:700;color:#777">ОТПРАВИТЕЛЬ<br/></span> Аптека №${pharmacyNum}
                ${documentType === 5 && toPharmacyNum ? `<br/><span style="font-weight:700;color:#777;margin-top:4px;display:inline-block">ПОЛУЧАТЕЛЬ<br/></span> Аптека №${toPharmacyNum}` : ''}
              </div>
            `}
            
            <div style="display:flex; justify-content:space-between; align-items:flex-end; margin-top:8px">
              <div style="
                font-family:monospace;
                font-size:0.6rem;
                line-height:1.2;
                color:#999;
                word-break:break-all;
     
                user-select:none;
              ">${box.box_code}</div>
              <div style="font-size:0.75rem;font-weight:600;color:#999;">${today}</div>
            </div>
          </div>
        </div>`;
    }).join('');

    // Открываем новое окно — один столбец на всю ширину
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR-коды коробов — №${documentNumber}</title>
        <style>
          @page { size: A4; margin: 8mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 8px; }
        </style>
      </head>
      <body>
        ${cardsHtml}
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Контент для печати */}
      <Box
        ref={printContentRef}
        sx={{
          flexGrow: 1,
          p: 3,
          overflow: 'auto'
        }}
      >
        {boxes.map((box) => (
          <Paper
            key={box.box_num}
            elevation={0}
            sx={{
              p: 2.5,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              border: '2px dashed #ccc',
              borderRadius: 3,
              width: '100%',
              bgcolor: 'white',
            }}
          >
            {/* QR-код слева */}
            <Box sx={{ flexShrink: 0, maxwidth: '100%', maxheight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <QRCodeSVG
                value={box.box_code}
                size={150}
                level="H"
                includeMargin={true}
              />
            </Box>

            {/* Информация справа */}
            < Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                {/* Номер документа — крупно */}
                <Typography
                  variant="h5"
                  fontWeight={900}
                  color="primary"
                  sx={{ fontSize: '1.2rem', lineHeight: 1 }}
                >
                  №{documentNumber}
                </Typography>

                {/* Номер короба */}
                <Typography
                  variant="subtitle2"
                  fontWeight={800}
                  sx={{ color: '#333', fontSize: '1rem', bgcolor: '#f5f5f5', borderRadius: 1, lineHeight: 1 }}
                >
                  Короб {box.box_num} / {boxes.length}
                </Typography>
              </Box>

              {/* Тип перемещения — выделено */}
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{
                  color: 'primary.main',
                  bgcolor: 'primary.50',
                  borderRadius: 1.5,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  display: 'inline-block',
                  alignSelf: 'flex-start',
                  my: 0.5
                }}
              >
                {movementLabel}
              </Typography>

              {/* Маршрут или отправитель/получатель */}
              {routeText ? (
                <Typography variant="body2" sx={{ color: '#444', fontSize: '1rem', mt: 1, lineHeight: 1.4 }}>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
                    МАРШРУТ
                  </Typography>
                  <span dangerouslySetInnerHTML={{ __html: routeText.replace(/ → /g, ' <span style="color:#1976d2;font-weight:bold">→</span> ') }} />
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: '#444', fontSize: '1rem', mt: 1, lineHeight: 1.4 }}>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block' }}>
                    ОТПРАВИТЕЛЬ
                  </Typography>
                  Аптека №{pharmacyNum}
                  {documentType === 5 && toPharmacyNum && (
                    <>
                      <Typography component="span" variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        ПОЛУЧАТЕЛЬ
                      </Typography>
                      Аптека №{toPharmacyNum}
                    </>
                  )}
                </Typography>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
                {/* Расшифровка кода */}
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    fontSize: '0.6rem',
                    lineHeight: 1.2,
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none'
                  }}
                >
                  {box.box_code}
                </Typography>

                {/* Дата */}
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.disabled' }}>
                  {new Date().toLocaleDateString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  })}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))
        }
      </Box >

      {/* Футер с кнопками */}
      < Box
        sx={{
          p: 2,
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'white',
        }}
      >
        <Button
          variant="outlined"
          onClick={onBack}
        >
          Назад
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            Печать
          </Button>
          <Button
            variant="contained"
            color="success"
            endIcon={<ArrowForwardIcon />}
            onClick={onNext}
          >
            Далее к сканированию
          </Button>
        </Box>
      </Box >
    </Box >
  );
};

export default BoxQRPrintView;
